import type {
  FieldValue,
  InputType,
  MultiSelectOption,
} from '@snapchat/snap-design-system-marketing';

import type { ContentfulSysProps } from '../../types/contentful';
import type { ContentfulInputField, DropdownOption, FormDataProps } from './types';

export const isFormDataProps = (props: ContentfulSysProps): props is FormDataProps =>
  (props as FormDataProps).__typename === 'Form';

export interface PresetDropdown {
  data: DropdownOption[];
  defaultPlaceholder?: string;
}

// Adapted from https://help.salesforce.com/s/articleView?id=sf.admin_state_country_picklists_standard_countries.htm
const countryCodeToName: Record<string, string> = {
  US: 'United States',
  AD: 'Andorra',
  AE: 'United Arab Emirates',
  AF: 'Afghanistan',
  AG: 'Antigua and Barbuda',
  AI: 'Anguilla',
  AL: 'Albania',
  AM: 'Armenia',
  AO: 'Angola',
  AQ: 'Antarctica',
  AR: 'Argentina',
  AT: 'Austria',
  AU: 'Australia',
  AW: 'Aruba',
  AX: 'Aland Islands',
  AZ: 'Azerbaijan',
  BA: 'Bosnia and Herzegovina',
  BB: 'Barbados',
  BD: 'Bangladesh',
  BE: 'Belgium',
  BF: 'Burkina Faso',
  BG: 'Bulgaria',
  BH: 'Bahrain',
  BI: 'Burundi',
  BJ: 'Benin',
  BL: 'Saint Barthélemy',
  BM: 'Bermuda',
  BN: 'Brunei',
  BO: 'Bolivia',
  BQ: 'Bonaire, Sint Eustatius and Saba',
  BR: 'Brazil',
  BS: 'Bahamas',
  BT: 'Bhutan',
  BV: 'Bouvet Island',
  BW: 'Botswana',
  BY: 'Belarus',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Cocos (Keeling) Islands',
  CD: 'Congo, the Democratic Republic of the',
  CF: 'Central African Republic',
  CG: 'Congo',
  CH: 'Switzerland',
  CI: "Cote d'Ivoire",
  CK: 'Cook Islands',
  CL: 'Chile',
  CM: 'Cameroon',
  CN: 'China',
  CO: 'Colombia',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cape Verde',
  CW: 'Curaçao',
  CX: 'Christmas Island',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DE: 'Germany',
  DJ: 'Djibouti',
  DK: 'Denmark',
  DM: 'Dominica',
  DO: 'Dominican Republic',
  DZ: 'Algeria',
  EC: 'Ecuador',
  EE: 'Estonia',
  EG: 'Egypt',
  EH: 'Western Sahara',
  ER: 'Eritrea',
  ES: 'Spain',
  ET: 'Ethiopia',
  FI: 'Finland',
  FJ: 'Fiji',
  FK: 'Falkland Islands (Malvinas)',
  FO: 'Faroe Islands',
  FR: 'France',
  GA: 'Gabon',
  GB: 'United Kingdom',
  GD: 'Grenada',
  GE: 'Georgia',
  GF: 'French Guiana',
  GG: 'Guernsey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Greenland',
  GM: 'Gambia',
  GN: 'Guinea',
  GP: 'Guadeloupe',
  GQ: 'Equatorial Guinea',
  GR: 'Greece',
  GS: 'South Georgia and the South Sandwich Islands',
  GT: 'Guatemala',
  GW: 'Guinea-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HM: 'Heard Island and McDonald Islands',
  HN: 'Honduras',
  HR: 'Croatia',
  HT: 'Haiti',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IM: 'Isle of Man',
  IN: 'India',
  IO: 'British Indian Ocean Territory',
  IQ: 'Iraq',
  IR: 'Iran',
  IS: 'Iceland',
  IT: 'Italy',
  JE: 'Jersey',
  JM: 'Jamaica',
  JO: 'Jordan',
  JP: 'Japan',
  KE: 'Kenya',
  KG: 'Kyrgyzstan',
  KH: 'Cambodia',
  KI: 'Kiribati',
  KM: 'Comoros',
  KN: 'Saint Kitts and Nevis',
  KP: "Korea, Democratic People's Republic of",
  KR: 'South Korea',
  KW: 'Kuwait',
  KY: 'Cayman Islands',
  KZ: 'Kazakhstan',
  LA: 'Laos',
  LB: 'Lebanon',
  LC: 'Saint Lucia',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  LY: 'Libya',
  MA: 'Morocco',
  MC: 'Monaco',
  MD: 'Moldova',
  ME: 'Montenegro',
  MF: 'Saint Martin',
  MG: 'Madagascar',
  MK: 'Macedonia',
  ML: 'Mali',
  MM: 'Myanmar',
  MN: 'Mongolia',
  MO: 'Macao',
  MQ: 'Martinique',
  MR: 'Mauritania',
  MS: 'Montserrat',
  MT: 'Malta',
  MU: 'Mauritius',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexico',
  MY: 'Malaysia',
  MZ: 'Mozambique',
  NA: 'Namibia',
  NC: 'New Caledonia',
  NE: 'Niger',
  NF: 'Norfolk Island',
  NG: 'Nigeria',
  NI: 'Nicaragua',
  NL: 'Netherlands',
  NO: 'Norway',
  NP: 'Nepal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'New Zealand',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Peru',
  PF: 'French Polynesia',
  PG: 'Papua New Guinea',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Poland',
  PM: 'Saint Pierre and Miquelon',
  PN: 'Pitcairn',
  PS: 'Palestine',
  PT: 'Portugal',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'Reunion',
  RO: 'Romania',
  RS: 'Serbia',
  RU: 'Russia',
  RW: 'Rwanda',
  SA: 'Saudi Arabia',
  SB: 'Solomon Islands',
  SC: 'Seychelles',
  SD: 'Sudan',
  SE: 'Sweden',
  SG: 'Singapore',
  SH: 'Saint Helena, Ascension and Tristan da Cunha',
  SI: 'Slovenia',
  SJ: 'Svalbard and Jan Mayen',
  SK: 'Slovakia',
  SL: 'Sierra Leone',
  SM: 'San Marino',
  SN: 'Senegal',
  SO: 'Somalia',
  SR: 'Suriname',
  SS: 'South Sudan',
  ST: 'Sao Tome and Principe',
  SV: 'El Salvador',
  SX: 'Sint Maarten (Dutch part)',
  SY: 'Syrian Arab Republic',
  SZ: 'Swaziland',
  TC: 'Turks and Caicos Islands',
  TD: 'Chad',
  TF: 'French Southern Territories',
  TG: 'Togo',
  TH: 'Thailand',
  TJ: 'Tajikistan',
  TK: 'Tokelau',
  TL: 'Timor-Leste',
  TM: 'Turkmenistan',
  TN: 'Tunisia',
  TO: 'Tonga',
  TR: 'Turkey',
  TT: 'Trinidad and Tobago',
  TV: 'Tuvalu',
  TW: 'Taiwan',
  TZ: 'Tanzania',
  UA: 'Ukraine',
  UG: 'Uganda',
  UY: 'Uruguay',
  UZ: 'Uzbekistan',
  VA: 'Holy See (Vatican City State)',
  VC: 'Saint Vincent and the Grenadines',
  VE: 'Venezuela',
  VG: 'Virgin Islands, British',
  VN: 'Vietnam',
  VU: 'Vanuatu',
  WF: 'Wallis and Futuna',
  WS: 'Samoa',
  YE: 'Yemen',
  YT: 'Mayotte',
  ZA: 'South Africa',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
};

// want to have sorted versions of countries
const sortedEntries = Object.entries(countryCodeToName).sort((a, b) =>
  // Making sure US is at the top first before sorting by country name
  a[0] === 'US' ? -1 : b[0] === 'US' ? 1 : a[1].localeCompare(b[1])
);

const sortedCountries: Record<string, string> = Object.fromEntries(sortedEntries);

export const presetDropdowns: Record<string, PresetDropdown> = {
  Countries: {
    defaultPlaceholder: 'Country',
    data: Object.values(sortedCountries).map(name => ({ key: name, value: name })),
  },
  'Countries (submit country code)': {
    defaultPlaceholder: 'Country',
    data: Object.keys(sortedCountries).map(code => ({
      key: countryCodeToName[code]!,
      value: code,
    })),
  },
};

/** Returns a field for a given name from within form props. */
export function getFormField(
  form: Pick<FormDataProps, 'rowsCollection'>,
  fieldName: string
): ContentfulInputField | undefined {
  for (const row of form.rowsCollection.items) {
    for (const field of row.fieldsCollection.items) {
      if (field.name === fieldName) {
        return field;
      }
    }
  }

  return undefined;
}

/** Map for matching contentful data to query parameters */
const utmParameterFieldMap = new Map(
  Object.entries({
    utmSourceFieldName: 'utm_source',
    utmMediumFieldName: 'utm_medium',
    utmCampaignFieldName: 'utm_campaign',
    utmContentFieldName: 'utm_content',
    utmTermFieldName: 'utm_term',
  })
);

/**
 * Parses Form Features configuration from Contentful for extra parameters to append to form payload
 *
 * @param input Form Features collection from Contentful
 * @param currentUrl String containing the current url used to retrieve query parameters
 * @returns Object containing extra parameters to append to form data
 */
export const getFormExtraParameters = (
  input: FormDataProps['additionalFeaturesCollection'],
  currentUrl: string
): Record<string, string> | undefined => {
  // only process the first matching entry
  const config = input?.items.find(item => item.__typename === 'ExtraParametersFormFeature');

  if (!config) return undefined;

  const searchParams = new URL(currentUrl).searchParams;

  const output: Record<string, string> = {};

  for (const [key, fieldName] of Object.entries(config)) {
    const parameter = utmParameterFieldMap.get(key);
    if (!fieldName || !parameter) continue; // if fieldname not defined or is missing from map, skip it

    output[fieldName] = searchParams.get(parameter) ?? '';
  }

  // Placeholder for future extra parameter logic (blizzard Identifier, etc)

  return output;
};

/**
 * Retrieves the form query parameters from the current URL checked against a form field
 * configuration.
 *
 * @returns A Map containing the form query parameters.
 */
export const getFormQueryParams = (
  currentUrl: string,
  getFormField: (fieldName: string) => ContentfulInputField | undefined
): Map<string, FieldValue> => {
  const params = new Map<string, string | string[]>();
  const url = new URL(currentUrl);
  const rawParams = new URLSearchParams(url.search);

  rawParams.forEach((value, key) => {
    if (key.startsWith('form_')) {
      const updatedKey = key.replace('form_', '');
      const value = rawParams.getAll(key);
      params.set(updatedKey, value.length > 1 ? value : value[0]!);
    }
  });

  // Now parse the params based on the field settings.

  const formBody = new Map<string, FieldValue>();

  for (const [key, value] of params) {
    const field = getFormField(key);
    if (!field || !field.name) continue;

    switch (field.__typename) {
      case 'DropdownField':
        formBody.set(field.name, value);
        break;

      case 'MultiselectDropdownField': {
        const values = Array.isArray(value) ? value : [value];
        const multiValues = values
          .map(val => {
            const optionName = field.optionsCollection?.items.find(item => item.key === val)?.value;
            if (!optionName) return undefined;
            return { id: val, name: optionName };
          })
          .filter(Boolean) as MultiSelectOption[];
        formBody.set(field.name!, multiValues);
        break;
      }

      case 'InputField': {
        const singleValue = Array.isArray(value) ? value[0] : value;

        switch (field.type as InputType) {
          case 'Checkbox': {
            formBody.set(field.name, singleValue === 'true');
            break;
          }

          case 'Number': {
            // TODO: Fix the types so that we can pass in numbers.
            formBody.set(field.name, singleValue);
            break;
          }

          // Works for Date, Hidden, Text and TextArea.
          default: {
            formBody.set(field.name, singleValue);
            break;
          }
        }
        break;
      }

      case 'PresetDropdownField': {
        formBody.set(field.name, value);
        break;
      }

      default: {
        console.warn(`Unsupported field type: ${field.__typename} for url form parsing.`);
        break;
      }
    }
  }

  return formBody;
};
